import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import chartStoryLogo from '../asset/Chart Story 72.png';
import './Landing.css';

const IMG = (name) => `${process.env.PUBLIC_URL}/landing/${name}`;

const CHAPTERS = [
  {
    tag: 'Chapter 01',
    title: 'The Market',
    text: 'Start with the big picture. Index moves, breadth and a live heatmap show where the day is leaning before you zoom in.',
    img: 'market-overview.webp',
  },
  {
    tag: 'Chapter 02',
    title: 'The Sector',
    text: 'Follow the rotation. Sector Radar and the Sector Calendar show which groups are gaining or losing relative strength.',
    img: 'sector-radar.webp',
  },
  {
    tag: 'Chapter 03',
    title: 'The Stock',
    text: 'Narrow it down. Screeners and stock summaries let you filter by technical conditions and study a single name in depth.',
    img: 'stock-insight.webp',
  },
  {
    tag: 'Chapter 04',
    title: 'The Time',
    text: 'Add a time lens. Market, Nakshatra and Gann-style calendars line up dates and cycles for those who research them.',
    img: 'calendar.webp',
  },
];

const BENTO = [
  { cls: 'lp-bento__item--wide', icon: '◐', title: 'Heatmap', text: 'See the whole market in colour, sector by sector.', img: 'heatmap.webp' },
  { cls: '', icon: '⌕', title: 'Screeners', text: 'Filter stocks on technical conditions.' },
  { cls: '', icon: '◎', title: 'Strategy Radar', text: 'Track how setups behave across the market.' },
  { cls: '', icon: '☾', title: 'Astro Insight', text: 'Planetary and Nakshatra studies, side by side with price.' },
  { cls: '', icon: '★', title: 'Watchlists', text: 'Build lists and track them with a daily snapshot.' },
  { cls: 'lp-bento__item--wide', icon: '▤', title: 'Research Desk', text: 'Expert views and a model portfolio for learning how analysts think.' },
];

const FAQ = [
  {
    q: 'Is this a tips or advisory service?',
    a: 'No. ChartStory · CS Insights Pro is an analytics and research tool. We do not give buy or sell calls, and nothing on the platform is investment advice.',
  },
  {
    q: 'Who is it for?',
    a: 'Traders, investors and students of the market who want to do their own research with organised data, charts and calendars.',
  },
  {
    q: 'Do astro and Gann studies predict the market?',
    a: 'No method can promise that. These are research lenses some analysts use to study timing. Treat them as one input, never as a guarantee.',
  },
  {
    q: 'How do I get access?',
    a: 'Create an account, verify your email, and sign in. Access to some sections depends on your plan.',
  },
];

// Seeded so the hero chart looks the same on every visit.
function makeCandles(count, seed = 7) {
  let s = seed;
  const rand = () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
  const out = [];
  let price = 100;
  for (let i = 0; i < count; i++) {
    const drift = Math.sin(i / 9) * 0.6 + 0.15;
    const open = price;
    const close = open + drift + (rand() - 0.5) * 3.2;
    const high = Math.max(open, close) + rand() * 1.6;
    const low = Math.min(open, close) - rand() * 1.6;
    out.push({ open, close, high, low });
    price = close;
  }
  return out;
}

function StoryChart() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const candles = makeCandles(64);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf;
    let start;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (shown) => {
      const { width: w, height: h } = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, w, h);

      const lo = Math.min(...candles.map((c) => c.low));
      const hi = Math.max(...candles.map((c) => c.high));
      const pad = 24;
      const y = (v) => pad + (1 - (v - lo) / (hi - lo)) * (h - pad * 2);
      const step = w / candles.length;

      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 5; i++) {
        const gy = (h / 5) * i;
        ctx.beginPath();
        ctx.moveTo(0, gy);
        ctx.lineTo(w, gy);
        ctx.stroke();
      }

      const full = Math.floor(shown);
      for (let i = 0; i < Math.min(full, candles.length); i++) {
        const c = candles[i];
        const x = i * step + step / 2;
        const up = c.close >= c.open;
        ctx.strokeStyle = ctx.fillStyle = up ? '#22d3a6' : '#f43f6b';
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.moveTo(x, y(c.high));
        ctx.lineTo(x, y(c.low));
        ctx.stroke();
        const bw = Math.max(2, step * 0.55);
        const top = y(Math.max(c.open, c.close));
        ctx.fillRect(x - bw / 2, top, bw, Math.max(1.5, y(Math.min(c.open, c.close)) - top));
      }
      ctx.globalAlpha = 1;

      if (full > 1) {
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, '#667eea');
        grad.addColorStop(1, '#c084fc');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2.5;
        ctx.shadowColor = 'rgba(167,139,250,0.8)';
        ctx.shadowBlur = 12;
        ctx.beginPath();
        for (let i = 0; i < Math.min(full, candles.length); i++) {
          const x = i * step + step / 2;
          const v = candles.slice(Math.max(0, i - 6), i + 1).reduce((a, c) => a + c.close, 0) /
            Math.min(i + 1, 7);
          if (i === 0) ctx.moveTo(x, y(v));
          else ctx.lineTo(x, y(v));
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

        const last = candles[Math.min(full, candles.length) - 1];
        const lx = (Math.min(full, candles.length) - 1) * step + step / 2;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(lx, y(last.close), 4, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = (t) => {
      if (!start) start = t;
      const elapsed = (t - start) / 1000;
      const cycle = candles.length / 14 + 2.5;
      const phase = elapsed % cycle;
      draw(Math.min(candles.length, phase * 14));
      raf = requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener('resize', resize);
    if (reduce) draw(candles.length);
    else raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="lp-chart__canvas" aria-hidden="true" />;
}

function Shot({ name, label }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="lp-shot__placeholder">
        <span>{label}</span>
        <small>/public/landing/{name}</small>
      </div>
    );
  }
  return <img src={IMG(name)} alt={label} loading="lazy" onError={() => setFailed(true)} />;
}

function NakshatraWheel() {
  const ticks = Array.from({ length: 27 });
  const signs = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
  return (
    <svg viewBox="0 0 400 400" className="lp-wheel" aria-hidden="true">
      <defs>
        <linearGradient id="lpWheelGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#667eea" />
          <stop offset="100%" stopColor="#c084fc" />
        </linearGradient>
      </defs>
      <g className="lp-wheel__outer">
        <circle cx="200" cy="200" r="190" fill="none" stroke="url(#lpWheelGrad)" strokeWidth="1.5" />
        {ticks.map((_, i) => {
          const a = (i / 27) * Math.PI * 2;
          return (
            <line
              key={i}
              x1={200 + Math.cos(a) * 172}
              y1={200 + Math.sin(a) * 172}
              x2={200 + Math.cos(a) * 190}
              y2={200 + Math.sin(a) * 190}
              stroke="rgba(192,132,252,0.7)"
              strokeWidth="1.5"
            />
          );
        })}
      </g>
      <g className="lp-wheel__inner">
        <circle cx="200" cy="200" r="150" fill="none" stroke="rgba(255,255,255,0.12)" />
        {signs.map((s, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          return (
            <text
              key={s}
              x={200 + Math.cos(a) * 128}
              y={200 + Math.sin(a) * 128}
              textAnchor="middle"
              dominantBaseline="central"
              fill="rgba(230,232,240,0.75)"
              fontSize="20"
            >
              {s}
            </text>
          );
        })}
        <circle cx="200" cy="200" r="100" fill="none" stroke="rgba(255,255,255,0.08)" />
      </g>
      <circle cx="200" cy="200" r="62" fill="rgba(102,126,234,0.12)" stroke="url(#lpWheelGrad)" />
    </svg>
  );
}

const Landing = () => {
  const { user, loading } = useAuth();
  const [active, setActive] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reveal = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-in')),
      { threshold: 0.15 }
    );
    root.querySelectorAll('[data-reveal]').forEach((el) => reveal.observe(el));

    const chapters = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && setActive(Number(e.target.dataset.index))),
      { rootMargin: '-45% 0px -45% 0px' }
    );
    root.querySelectorAll('[data-chapter]').forEach((el) => chapters.observe(el));

    return () => {
      reveal.disconnect();
      chapters.disconnect();
    };
  }, [loading, user]);

  const trackGlow = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  if (loading) return null;
  if (user) return <Navigate to="/market-insight" replace />;

  return (
    <div className="lp" ref={rootRef}>
      <div className="lp-aurora" aria-hidden="true" />

      <header className="lp-nav">
        <div className="lp-container lp-nav__inner">
          <Link to="/" className="lp-brand">
            <img src={chartStoryLogo} alt="" className="lp-brand__logo" />
            <span>
              ChartStory <em>·</em> CS Insights Pro
            </span>
          </Link>
          <nav className="lp-nav__links">
            <a href="#story">Story</a>
            <a href="#tools">Tools</a>
            <a href="#time">Time</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="lp-nav__actions">
            <Link to="/login" className="lp-btn lp-btn--ghost">Sign in</Link>
            <Link to="/register" className="lp-btn lp-btn--primary">Get started</Link>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-container lp-hero__grid">
            <div className="lp-hero__copy">
              <span className="lp-badge">
                <i className="lp-pulse" /> Research workspace for Indian equities
              </span>
              <h1 className="lp-hero__title">
                Every chart
                <br />
                tells a <span className="lp-gradient">story.</span>
              </h1>
              <p className="lp-hero__sub">
                Market, sector, stock and time — read all four chapters in one place.
                Heatmaps, screeners, sector rotation and market calendars, built for
                people who do their own research.
              </p>
              <div className="lp-hero__cta">
                <Link to="/register" className="lp-btn lp-btn--primary lp-btn--lg">
                  Create free account <span aria-hidden="true">→</span>
                </Link>
                <Link to="/login" className="lp-btn lp-btn--ghost lp-btn--lg">Sign in</Link>
              </div>
              <p className="lp-hero__note">A research tool, not a tip service. No buy or sell calls.</p>
            </div>

            <div className="lp-chart" data-reveal>
              <div className="lp-chart__bar">
                <span className="lp-dot" /><span className="lp-dot" /><span className="lp-dot" />
                <span className="lp-chart__label">Illustrative chart</span>
              </div>
              <StoryChart />
              <div className="lp-chart__chip lp-chart__chip--a">Sector rotation</div>
              <div className="lp-chart__chip lp-chart__chip--b">Time cycle</div>
              <div className="lp-chart__chip lp-chart__chip--c">Breadth</div>
            </div>
          </div>

          <div className="lp-marquee" aria-hidden="true">
            <div className="lp-marquee__track">
              {[0, 1].map((k) => (
                <span key={k}>
                  Market Overview ✦ Heatmap ✦ Sector Radar ✦ Sector Calendar ✦ Screeners ✦
                  Strategy Radar ✦ Astro Insight ✦ Nakshatra ✦ Time Trend Shift ✦ Combined
                  Calendar ✦ Watchlists ✦ Research Desk ✦&nbsp;
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-story" id="story">
          <div className="lp-container">
            <h2 className="lp-section__title" data-reveal>
              Read the market like a <span className="lp-gradient">book</span>
            </h2>
            <p className="lp-section__sub" data-reveal>
              Four chapters, one workspace. Scroll to turn the page.
            </p>

            <div className="lp-story__grid">
              <div className="lp-story__chapters">
                {CHAPTERS.map((c, i) => (
                  <article
                    key={c.title}
                    data-chapter
                    data-index={i}
                    className={`lp-chapter ${active === i ? 'is-active' : ''}`}
                  >
                    <span className="lp-chapter__tag">{c.tag}</span>
                    <h3>{c.title}</h3>
                    <p>{c.text}</p>
                    <div className="lp-chapter__shot-mobile">
                      <Shot name={c.img} label={c.title} />
                    </div>
                  </article>
                ))}
              </div>

              <div className="lp-story__stage">
                <div className="lp-frame">
                  <div className="lp-frame__bar">
                    <span className="lp-dot" /><span className="lp-dot" /><span className="lp-dot" />
                    <span className="lp-frame__url">chartstoryipro.com</span>
                  </div>
                  <div className="lp-frame__body">
                    {CHAPTERS.map((c, i) => (
                      <div key={c.img} className={`lp-frame__slide ${active === i ? 'is-active' : ''}`}>
                        <Shot name={c.img} label={c.title} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lp-progress">
                  {CHAPTERS.map((c, i) => (
                    <span key={c.title} className={active === i ? 'is-active' : ''} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section" id="tools">
          <div className="lp-container">
            <h2 className="lp-section__title" data-reveal>
              The toolkit
            </h2>
            <p className="lp-section__sub" data-reveal>
              Everything sits behind one login, organised the way analysts actually work.
            </p>
            <div className="lp-bento">
              {BENTO.map((b) => (
                <article
                  key={b.title}
                  className={`lp-bento__item ${b.cls}`}
                  onMouseMove={trackGlow}
                  data-reveal
                >
                  <div className="lp-bento__icon" aria-hidden="true">{b.icon}</div>
                  <h3>{b.title}</h3>
                  <p>{b.text}</p>
                  {b.img && (
                    <div className="lp-bento__shot">
                      <Shot name={b.img} label={b.title} />
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-time" id="time">
          <div className="lp-container lp-time__grid">
            <div className="lp-time__wheel" data-reveal>
              <NakshatraWheel />
              <img src={chartStoryLogo} alt="" className="lp-time__logo" />
            </div>
            <div data-reveal>
              <span className="lp-chapter__tag">The time lens</span>
              <h2 className="lp-section__title lp-section__title--left">
                Where <span className="lp-gradient">price</span> meets{' '}
                <span className="lp-gradient">time</span>
              </h2>
              <p className="lp-time__text">
                Most platforms stop at price. We add a calendar of dates, cycles and
                Nakshatra transitions so you can study timing alongside the chart — a
                research lens you will rarely find in one place.
              </p>
              <ul className="lp-time__list">
                <li>27 Nakshatras mapped to the trading calendar</li>
                <li>Gann-style time and reversal studies</li>
                <li>Combined calendar: market, sector and astro events together</li>
              </ul>
              <p className="lp-time__fine">
                These are study methods, not predictions. No method guarantees an outcome.
              </p>
            </div>
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-container">
            <h2 className="lp-section__title" data-reveal>Start in three steps</h2>
            <div className="lp-steps">
              {['Create an account', 'Verify your email', 'Open your dashboard'].map((t, i) => (
                <div key={t} className="lp-step" data-reveal>
                  <div className="lp-step__num">{i + 1}</div>
                  <h3>{t}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section" id="faq">
          <div className="lp-container lp-faq">
            <h2 className="lp-section__title" data-reveal>Questions</h2>
            {FAQ.map((f, i) => (
              <div key={f.q} className={`lp-faq__item ${openFaq === i ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="lp-faq__q"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  {f.q}
                  <span aria-hidden="true">+</span>
                </button>
                <div className="lp-faq__a">
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-container">
            <div className="lp-final" data-reveal>
              <h2>
                Your next chapter starts <span className="lp-gradient">here.</span>
              </h2>
              <p>Free to sign up. Takes under a minute.</p>
              <div className="lp-hero__cta">
                <Link to="/register" className="lp-btn lp-btn--primary lp-btn--lg">
                  Create free account <span aria-hidden="true">→</span>
                </Link>
                <Link to="/login" className="lp-btn lp-btn--ghost lp-btn--lg">Sign in</Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container">
          <p className="lp-disclaimer">
            <strong>Disclaimer:</strong> ChartStory · CS Insights Pro is an analytics and
            educational tool. Nothing on this platform is investment advice or a
            recommendation to buy or sell any security. We are not a SEBI-registered Research
            Analyst or Investment Adviser. Astro, time-cycle and Gann studies are research
            methods with no assurance of accuracy. Past patterns do not guarantee future
            results. Please consult a SEBI-registered adviser before investing.
          </p>
          <div className="lp-footer__bottom">
            <span>© {new Date().getFullYear()} ChartStory · CS Insights Pro</span>
            <span className="lp-footer__links">
              <Link to="/login">Sign in</Link>
              <Link to="/register">Register</Link>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
